import { useState, type ComponentType } from 'react'
import type { DiplomacyDirection, DiplomacyStateDto } from '@/api/hooks'
import { useCountdown } from '@/hooks/useCountdown'
import { AlignmentGauge } from '@/components/AlignmentGauge'
import { Card, GoldButton, ResourceTally } from '@/components/game/ui'
import { AnchorIcon, SkullIcon } from '@/components/game/GameIcons'

/**
 * Le panneau de la table des négociations : jauge avec prévisualisation, choix
 * du geste (Corruption ↔ Diplomatie), curseur 1-3 points, devis et CTA.
 * Présentationnel — données et mutation vivent dans la page Negotiations.
 */
export function NegotiationsPanel({
  state,
  gold,
  pending,
  onNegotiate,
}: {
  state: DiplomacyStateDto
  /** Or dispo (base serveur) pour griser le CTA — le serveur reste l'autorité. */
  gold: number | null
  pending: boolean
  onNegotiate: (direction: DiplomacyDirection, points: number) => void
}) {
  const [direction, setDirection] = useState<DiplomacyDirection>('diplomacy')
  const [points, setPoints] = useState(1)

  const camp = state.camps[direction]
  const max = camp.maxPoints
  const n = Math.max(1, Math.min(points, Math.max(1, max)))
  const total = max > 0 ? camp.costs[Math.min(n, max) - 1] : 0
  const preview = max > 0 ? state.loyalty + (direction === 'diplomacy' ? n : -n) : null

  const countdown = useCountdown(state.availableAt)
  const onCooldown = state.availableAt !== null && new Date(state.availableAt).getTime() > Date.now()
  const cantAfford = !onCooldown && max > 0 && gold !== null && total > gold
  const disabled = pending || onCooldown || max === 0 || cantAfford

  const reason = onCooldown
    ? `Prochaine démarche dans ${countdown}`
    : max === 0
      ? 'Au-delà de ±300, seuls tes actes comptent'
      : cantAfford
        ? 'Or insuffisant'
        : null

  const perPoint = max > 0
    ? camp.costs.slice(0, n).map((c, i) => (i === 0 ? c : c - camp.costs[i - 1]))
    : []

  return (
    <Card className="p-5 sm:p-6 space-y-5">
      <AlignmentGauge loyalty={state.loyalty} preview={preview} />

      <div className="grid grid-cols-2 gap-3">
        <CampButton
          active={direction === 'corruption'}
          onClick={() => setDirection('corruption')}
          Icon={SkullIcon}
          title="Corruption"
          subtitle="vers hors-la-loi"
          activeClass="border-rum-500/70 bg-rum-700/25"
        />
        <CampButton
          active={direction === 'diplomacy'}
          onClick={() => setDirection('diplomacy')}
          Icon={AnchorIcon}
          title="Diplomatie"
          subtitle="vers loyal"
          activeClass="border-ocean-300/70 bg-ocean-700/30"
        />
      </div>

      <div className="flex items-center gap-3">
        <span className="text-xs uppercase tracking-wider text-ocean-300 whitespace-nowrap">Points</span>
        <input
          type="range"
          min={1}
          max={Math.max(1, max)}
          step={1}
          value={n}
          disabled={max === 0}
          onChange={(e) => setPoints(Number(e.target.value))}
          className="flex-1 accent-sand-300 disabled:opacity-40"
          aria-label="Points d'alignement"
          aria-valuetext={max === 0 ? 'Aucun point achetable' : `${n} point${n > 1 ? 's' : ''} pour ${total.toLocaleString('fr-FR')} or`}
        />
        <span className="font-display text-sand-100 tabular-nums w-4 text-center">{max === 0 ? '—' : n}</span>
      </div>

      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs text-ocean-300 tabular-nums truncate">
          {perPoint.length > 1 ? perPoint.map((c) => c.toLocaleString('fr-FR')).join(' + ') : ' '}
        </span>
        <ResourceTally gold={total} className="font-display text-lg text-sand-100" iconClassName="w-4 h-4 text-sand-300" />
      </div>

      <GoldButton className="w-full px-4 py-2.5" disabled={disabled} onClick={() => onNegotiate(direction, n)}>
        {pending ? 'Négociation…' : "Sceller l'accord"}
      </GoldButton>
      {reason && <p className="text-xs text-center text-ocean-300">{reason}</p>}
    </Card>
  )
}

function CampButton({
  active,
  onClick,
  Icon,
  title,
  subtitle,
  activeClass,
}: {
  active: boolean
  onClick: () => void
  Icon: ComponentType<{ className?: string }>
  title: string
  subtitle: string
  activeClass: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-xl border px-3 py-2.5 text-left transition-colors ${
        active ? activeClass : 'border-sand-500/20 bg-ocean-900/30 hover:border-sand-500/40'
      }`}
    >
      <span className="flex items-center gap-2 font-display text-sand-100">
        <Icon className="w-4 h-4 text-sand-300" />
        {title}
      </span>
      <span className="block text-[11px] text-ocean-300 mt-0.5">{subtitle}</span>
    </button>
  )
}
